import React, { useEffect, useState, useRef } from 'react';
import { Bell, X, CheckCheck } from 'lucide-react';
import { useCurrentUser } from '@/components/auth/AuthProvider';
import {
  getNotifications, getUnreadCount, markAllRead,
  requestNotificationPermission, subscribeToPush, registerServiceWorker
} from '../../services/notificationService';

interface Notification {
  id: string;
  title: string;
  body: string;
  type: string;
  read: boolean;
  createdAt: string;
}

export function NotificationBell() {
  const user = useCurrentUser();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
  if (!user) return;

  // One-time setup — only runs once on mount
  async function setup() {
    await registerServiceWorker();
    setPermissionGranted(Notification.permission === 'granted');
    await loadNotifications();
  }

  setup();

  // Poll for new notifications every 60 seconds — does NOT re-register or re-request permission
  const interval = setInterval(() => {
    loadNotifications();
  }, 60000);

  return () => clearInterval(interval);
}, [user?.id]); // only re-run if user ID changes, not on every render

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function loadNotifications() {
  if (!user) return;
  try {
    const [notifs, count] = await Promise.all([
      getNotifications(user.id),
      getUnreadCount(user.id),
    ]);
    setNotifications(notifs);
    setUnreadCount(count);
  } catch (err) {
    console.error('Failed to load notifications:', err);
  }
}

  async function handleEnableNotifications() {
    const granted = await requestNotificationPermission();
    if (granted && user) {
      setPermissionGranted(true);
      await subscribeToPush(user.id, user.email);
    }
  }

  async function handleMarkAllRead() {
    if (!user) return;
    await markAllRead(user.id);
    setUnreadCount(0);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative rounded-lg p-2 text-gray-400 transition-colors hover:bg-white/10 hover:text-white"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-status-fail text-[10px] font-bold text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-10 z-50 w-80 rounded-xl border border-gray-200 bg-white shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <h3 className="font-semibold text-gray-800">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="rounded p-1 text-gray-400 hover:bg-gray-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Enable push notifications prompt */}
          {!permissionGranted && (
            <div className="border-b border-gray-100 bg-wire3-navy/5 px-4 py-3">
              <p className="text-xs text-gray-600">
                Enable push notifications to get alerts when you're away from the app.
              </p>
              <button
                onClick={handleEnableNotifications}
                className="mt-2 rounded-lg bg-wire3-navy px-3 py-1.5 text-xs font-semibold text-white hover:bg-wire3-navy-light"
              >
                Enable Notifications
              </button>
            </div>
          )}

          {/* Notification list */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center py-8 text-gray-400">
                <Bell className="mb-2 h-8 w-8" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              notifications.map(n => (
                <div
                  key={n.id}
                  className={`border-b border-gray-50 px-4 py-3 last:border-0 ${
                    !n.read ? 'bg-wire3-navy/5' : ''
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`mt-1 h-2 w-2 shrink-0 rounded-full ${
                      !n.read ? 'bg-wire3-green' : 'bg-transparent'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800">{n.title}</p>
                      <p className="text-xs text-gray-500">{n.body}</p>
                      <p className="mt-1 text-[10px] text-gray-400">
                        {new Date(n.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}